import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { MACRO_BINDING_KEYS } from "../MacroBindingContext";

export interface MacroHtmlEditorModalProps {
  isOpen: boolean;
  initialValue: string;
  onClose: () => void;
  onSave: (value: string) => void;
}

export function MacroHtmlEditorModal(props: MacroHtmlEditorModalProps) {
  const { isOpen, initialValue, onClose, onSave } = props;
  const [value, setValue] = useState(initialValue);
  const [activeTab, setActiveTab] = useState<"edit" | "preview">("edit");

  useEffect(() => {
    if (isOpen) {
      setValue(initialValue);
      setActiveTab("edit");
    }
  }, [isOpen, initialValue]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const insertVariable = (variable: string) => {
    const textarea = document.getElementById(
      "html-editor-textarea"
    ) as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue =
        value.substring(0, start) + `{{${variable}}}` + value.substring(end);
      setValue(newValue);
      // Restore focus and cursor position
      setTimeout(() => {
        textarea.focus();
        const newPos = start + variable.length + 4; // +4 for {{ and }}
        textarea.setSelectionRange(newPos, newPos);
      }, 0);
    }
  };

  const insertHtmlSnippet = (snippet: string) => {
    const textarea = document.getElementById(
      "html-editor-textarea"
    ) as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = value.substring(start, end);

      // If text is selected, wrap it; otherwise just insert
      let newContent: string;
      if (selectedText && snippet.includes("$1")) {
        newContent = snippet.replace("$1", selectedText);
      } else {
        newContent = snippet.replace("$1", "");
      }

      const newValue = value.substring(0, start) + newContent + value.substring(end);
      setValue(newValue);
      setTimeout(() => {
        textarea.focus();
      }, 0);
    }
  };

  // Simple preview with variable placeholders shown
  const previewHtml = value;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex bg-black/50">
      <div className="flex h-full w-full flex-col bg-white dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
            Edit HTML Content
          </h2>
          <div className="flex items-center gap-4">
            {/* Tabs */}
            <div className="flex rounded-md border border-gray-300 dark:border-gray-600">
              <button
                type="button"
                onClick={() => setActiveTab("edit")}
                className={`px-3 py-1 text-xs font-medium ${
                  activeTab === "edit"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300"
                } rounded-l-md`}
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("preview")}
                className={`px-3 py-1 text-xs font-medium ${
                  activeTab === "preview"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300"
                } rounded-r-md`}
              >
                Preview
              </button>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-xs text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Close
            </button>
          </div>
        </div>

        {/* Toolbar - only show in edit mode */}
        {activeTab === "edit" && (
          <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2 dark:border-gray-700">
            <span className="text-[10px] font-medium text-gray-500 uppercase">
              Variables:
            </span>
            {MACRO_BINDING_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => insertVariable(key)}
                className="rounded border border-gray-300 bg-white px-2 py-0.5 text-[10px] text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300"
              >
                {`{{${key}}}`}
              </button>
            ))}
            <span className="mx-2 text-gray-300">|</span>
            <span className="text-[10px] font-medium text-gray-500 uppercase">
              HTML:
            </span>
            <button
              type="button"
              onClick={() => insertHtmlSnippet("<strong>$1</strong>")}
              className="rounded border border-gray-300 bg-white px-2 py-0.5 text-[10px] font-bold text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300"
              title="Bold"
            >
              B
            </button>
            <button
              type="button"
              onClick={() => insertHtmlSnippet("<em>$1</em>")}
              className="rounded border border-gray-300 bg-white px-2 py-0.5 text-[10px] italic text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300"
              title="Italic"
            >
              I
            </button>
            <button
              type="button"
              onClick={() => insertHtmlSnippet("<u>$1</u>")}
              className="rounded border border-gray-300 bg-white px-2 py-0.5 text-[10px] underline text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300"
              title="Underline"
            >
              U
            </button>
            <button
              type="button"
              onClick={() => insertHtmlSnippet("<br />")}
              className="rounded border border-gray-300 bg-white px-2 py-0.5 text-[10px] text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300"
              title="Line break"
            >
              BR
            </button>
            <button
              type="button"
              onClick={() => insertHtmlSnippet("<p>$1</p>")}
              className="rounded border border-gray-300 bg-white px-2 py-0.5 text-[10px] text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300"
              title="Paragraph"
            >
              P
            </button>
            <button
              type="button"
              onClick={() => insertHtmlSnippet('<a href="">$1</a>')}
              className="rounded border border-gray-300 bg-white px-2 py-0.5 text-[10px] text-blue-600 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600"
              title="Link"
            >
              Link
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex flex-1 flex-col overflow-hidden p-4">
          {activeTab === "edit" ? (
            <textarea
              id="html-editor-textarea"
              className="flex-1 resize-none rounded border border-gray-300 bg-white px-3 py-2 font-mono text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Enter your HTML content here..."
              spellCheck={false}
            />
          ) : (
            <div className="flex-1 overflow-auto rounded border border-gray-300 bg-white p-4 dark:bg-gray-800 dark:border-gray-600">
              <div
                className="prose prose-sm max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t px-4 py-3 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-gray-300 bg-white px-4 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(value)}
            className="rounded bg-blue-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );

  // Use portal to render at document root
  return ReactDOM.createPortal(modalContent, document.body);
}
